// Partner (internal), Markets. Government yield curves, relative value, credit spreads, live
// prices and data health.
//
// TIER. Markets is INVESTMENT TEAM ONLY (ruling 2026-09-04, which narrowed the staff-wide decision
// of 2026-08-27). It briefly lived at /adviser/markets; that tab was removed when both market
// surfaces were moved internal. This file therefore sits in the internal-only route graph and may
// freely reference proprietary data, which the adviser and public bundles must never see.
//
// The gate is in TWO places and they must be changed together: canInvestmentRoutes around the
// navbar link in PartnerNavbar.tsx, and requiredTeam="Investment" on the route in
// partnerOpsRoutes.tsx. Both resolve to the same allow set (Investment team, admin, superadmin).
// Gating only the link would leave this page reachable by URL to any signed-in staff member.
//
// DATA PATH. This page never reads research_fred_observations directly. That table's RLS is
// can_read_research(), which resolves to team='Investment' OR admin/superadmin, so an Operations or
// Advisory staff member would get zero rows. The curve, relative-value and credit reads go through
// SECURITY DEFINER RPCs instead, each bounded by its own allow-list.
//
// ⚠️ The IG live-price panel is NOT definer-gated: markets_strip_v1 filters on is_investment_team()
// in the database, so a non-investment staff member legitimately gets an empty list. That panel
// must say so rather than render blank.
//
// NEVER FABRICATE. A tenor the source did not publish is absent, not interpolated. The curve-shape
// RPC returns the most recent date at which a curve is COMPLETE, so a curve is never assembled out
// of points from different days. Every chart states its own as-at date and its source.

import { Fragment, useCallback, useId, useMemo, useState, type ReactNode } from "react";
import {
  CartesianGrid, Line, LineChart, ReferenceLine,
  XAxis, YAxis, LabelList,
} from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { useCachedQuery } from "@/lib/offline/cachedQuery";
import { sparklinePoints, rangeFraction } from "@/lib/sparkline";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { AlertTriangle, ChevronRight, TableIcon, LineChart as LineChartIcon } from "lucide-react";
import { useMarketData } from "@/hooks/useMarketData";
import { MarketStrip } from "@/components/market/MarketStrip";

// ---------------------------------------------------------------------------
// Curves
//
// Palette note: the series colours are NOT the brand token. --primary (#004742) fails a
// categorical-palette check on two counts (OKLCH lightness 0.358, below the 0.43 floor, and chroma
// 0.063, which reads as grey). These steps were validated with the dataviz palette checker in both
// modes: worst adjacent CVD deltaE 13.1 light and 12.5 dark, against a target of 8.
//
// The dark steps are insurance, not the common case. ThemeProvider sets forcedTheme="light" on
// web (src/components/theme/ThemeProvider.tsx), so a browser NEVER puts the `dark` class on <html>
// however the OS is set. Dark only resolves inside the native Capacitor app, which composes these
// routes through AppInternal. Do not "simplify" the dark values away on the assumption they are
// unreachable, and do not force the class by hand to test them: that leaves the whole site in a
// state the web app cannot otherwise reach.
// ---------------------------------------------------------------------------

type CurveCode = "UST" | "BUND" | "UKGLC" | "OAT";

// The three UK measures the Bank publishes in the SAME zip as the nominal curve.
type UkCode = "UKGLC" | "UKRLC" | "UKINF" | "UKOIS";

type SourceCode = CurveCode | UkCode;

// Attribution, keyed by CURVE rather than by chart.
//
// Every publisher here requires attribution wherever the data is shown, and FRED asks that the
// originating source be named alongside FRED itself, which is why the UST line credits the Federal
// Reserve H.15 release and not just the redistributor. Attribution used to live on each chart's own
// definition list, which worked while every surface plotted whole curves. The relative-value strip
// does not: its rows are spreads BETWEEN two publishers' curves, so it has to name every publisher
// a row leans on, and a second copy of these strings next to it would drift from the first. One
// record, read by both.
const CURVE_ATTRIBUTION: Record<SourceCode, { label: string; source: string }> = {
  UST:   { label: "US Treasury",          source: "Federal Reserve H.15 via FRED, constant maturity" },
  BUND:  { label: "German Bund",          source: "Deutsche Bundesbank, Svensson zero-coupon spot" },
  UKGLC: { label: "UK gilt",              source: "Bank of England GLC zero-coupon spot, Open Government Licence" },
  OAT:   { label: "French OAT",           source: "Banque de France TEC, constant maturity" },
  UKRLC: { label: "UK real",              source: "Bank of England GLC real zero-coupon spot, Open Government Licence" },
  UKINF: { label: "UK implied inflation", source: "Bank of England GLC implied inflation, Open Government Licence" },
  UKOIS: { label: "SONIA OIS",            source: "Bank of England SONIA OIS zero-coupon spot, Open Government Licence" },
};

type CurveDef<T extends string> = {
  code: T; label: string; short: string; light: string; dark: string;
};

const CURVES: CurveDef<CurveCode>[] = [
  { code: "UST",   label: "US Treasury",   short: "US",   light: "#2563EB", dark: "#3B82F6" },
  { code: "BUND",  label: "German Bund",   short: "Bund", light: "#B45309", dark: "#BE8309" },
  { code: "UKGLC", label: "UK gilt",       short: "Gilt", light: "#0E9384", dark: "#0D9488" },
  { code: "OAT",   label: "French OAT",    short: "OAT",  light: "#7C3AED", dark: "#8B5CF6" },
];

// The euro area AAA curve (curve_code EA_AAA, ECB) is ingested daily and reachable through the
// same RPCs, but is deliberately not plotted: at every tenor it sits within a few basis points of
// the Bund curve, so the two lines overlay and the chart reads worse for having both. It is absent
// from CURVE_ATTRIBUTION for the same reason, since nothing on this page displays it.

// Nominal keeps the gilt teal it wears on the country chart; the other two are their own validated
// steps (worst adjacent CVD deltaE 13.4 light, 12.1 dark).
const UK_DECOMP: CurveDef<UkCode>[] = [
  { code: "UKGLC", label: "Nominal",           short: "Nom",  light: "#0E9384", dark: "#0D9488" },
  { code: "UKRLC", label: "Real",              short: "Real", light: "#BE123C", dark: "#E11D48" },
  { code: "UKINF", label: "Implied inflation", short: "Infl", light: "#4338CA", dark: "#6366F1" },
];

const UK_VS_OIS: CurveDef<UkCode>[] = [
  { code: "UKGLC", label: "Gilt nominal", short: "Gilt", light: "#0E9384", dark: "#0D9488" },
  { code: "UKOIS", label: "SONIA OIS",    short: "OIS",  light: "#A16207", dark: "#BE8309" },
];

// Which published curves each relative-value row is built from. market_rv_strip_v1 returns the
// measure but not its inputs, so this mirrors the `defs` CTE in that function and has to be changed
// with it. A key absent here contributes no attribution line, which is why the strip's footnote
// states the rule rather than leaving a silent gap.
const RV_MEASURE_CURVES: Record<string, readonly SourceCode[]> = {
  UST10_BUND10:  ["UST", "BUND"],
  GILT10_BUND10: ["UKGLC", "BUND"],
  OAT10_BUND10:  ["OAT", "BUND"],
  GILT10_UST10:  ["UKGLC", "UST"],
  UST_2S10S:     ["UST"],
  GILT_2S10S:    ["UKGLC"],
  BUND_2S10S:    ["BUND"],
  GILT10_OIS10:  ["UKGLC", "UKOIS"],
  UKRLC_10Y:     ["UKRLC"],
  UKINF_10Y:     ["UKINF"],
};

// The credit strip's seven series carry no curve_code and no tenor, so they attribute as one line
// rather than per row. Both the index provider and FRED are named because the data reaches us
// through FRED and the indices are not FRED's.
const CREDIT_SOURCES = [
  { label: "ICE BofA index OAS", source: "ICE Data Indices LLC, retrieved from FRED, Federal Reserve Bank of St Louis" },
];

function configFor<T extends string>(defs: CurveDef<T>[]): ChartConfig {
  return Object.fromEntries(
    defs.map((c) => [c.code, { label: c.label, theme: { light: c.light, dark: c.dark } }]),
  );
}

// Scoped per card, NOT merged. UKGLC appears in all three definition lists under a different label
// each time ("UK gilt" on the country chart, "Nominal" in the decomposition, "Gilt nominal" against
// OIS). A single merged config let the last definition win and relabelled the country chart.
const chartConfig: ChartConfig = configFor(CURVES);

// ---------------------------------------------------------------------------
// Queries
//
// The three market_curve_*_v1 RPCs post-date the last types.ts generation, so the client is cast
// at the call boundary. Same pattern and same reason as useMarketData's markets_strip_v1 call.
// ---------------------------------------------------------------------------

// curve_code is the RPC's own text column, not narrowed to CurveCode: the shape call asks for every
// curve, so the four UK measures come back in the same payload and the UK cards read them from it.
type ShapeRow = { curve_code: string; as_of: string; tenor_years: number; value: number };
// on_common_grid is false only for a point past the end of the shared comparison calendar, which is
// how a curve that published earlier today than the others still reaches the chart. See the
// p_align_curves note on the history query below.
type HistRow = {
  tenor_years: number; observation_date: string; value: number; on_common_grid: boolean;
};
// Suffix marking a reshaped point as off the shared calendar. A suffix rather than a nested object
// because Recharts reads chart rows as a flat bag of dataKeys, and a curve code is never a prefix
// of another curve code, so `UST__offGrid` cannot collide with a real series.
const OFF_GRID_SUFFIX = "__offGrid";
type RvRow = {
  measure_key: string; label: string; unit: "bp" | "pct";
  as_of: string; current_value: number; pctile: number;
  window_min: number; window_max: number; window_median: number;
  n_obs: number; spark: number[]; spark_from: string; spark_to: string;
};
// One expanded row. market_measure_history_v1 returns exactly ONE row per call, or none for an
// unknown key, because the whole path travels as a single jsonb array: US 2s10s at max is 12,562
// observations, and a row-per-observation shape would have been truncated at PostgREST's 1000-row
// ceiling and drawn a line that simply stopped in 1979.
//
// The four fields a caller must not confuse:
//   current_value  the HEADLINE number, already carrying the strip's own rounding. Reading the
//                  last element of `points` instead rounds a rounded number a second time.
//   window_from/to the TRUE first and last observation in the window, over all n_obs. NOT the
//                  ends of the sampled path, which start later whenever the stride drops the
//                  oldest observations.
//   requested_from the cut date the button asked for, null for Max. Compared against window_from
//                  it is how the panel knows whether the button was honoured.
//   sampled        n_points < n_obs. Never inferred from a row count: the stride is ceil(n/max),
//                  so a sampled series routinely lands well under the budget.
type MeasurePoint = { observation_date: string; value: number };
type MeasureHistoryRow = {
  measure_key: string; label: string; unit: "bp" | "pct"; strip: string;
  as_of: string; current_value: number; pctile: number;
  window_min: number; window_max: number; window_median: number;
  n_obs: number; window_from: string; window_to: string;
  requested_years: number | null; requested_from: string | null;
  sampled: boolean; n_points: number; points: MeasurePoint[] | null;
};
type FreshnessRow = {
  series_id: string; curve_code: string | null; feed_provider: string | null;
  last_observation_date: string | null; days_since_obs: number | null; status: string;
};
type CatalogRow = {
  curve_code: CurveCode; feed_provider: string; tenor_count: number;
  min_tenor: number; max_tenor: number; as_of: string | null; active: boolean;
};

const db = supabase as unknown as {
  rpc: (fn: string, args?: Record<string, unknown>) => Promise<{ data: unknown; error: unknown }>;
};

async function callRpc<T>(fn: string, args?: Record<string, unknown>): Promise<T[]> {
  const { data, error } = await db.rpc(fn, args);
  if (error) throw error instanceof Error ? error : new Error(String((error as { message?: string }).message ?? fn));
  return (data ?? []) as T[];
}

// Built with the getUTC* twins and Date.UTC so the boundary cannot land a day either side
// depending on the reader's zone. Date.UTC normalises a negative month for us.
function isoMonthsAgo(months: number): string {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - months, now.getUTCDate()))
    .toISOString().slice(0, 10);
}

const AS_OF_CHOICES = [
  { key: "now", label: "Latest", months: 0 },
  { key: "m3", label: "3 months ago", months: 3 },
  { key: "m12", label: "12 months ago", months: 12 },
] as const;

const HISTORY_WINDOWS = [
  { key: "1y", label: "1 year", years: 1 },
  { key: "5y", label: "5 years", years: 5 },
  { key: "max", label: "Since 2005", years: 100 },
] as const;

// Windows offered on an expanded strip row. `years: null` is Max, which market_measure_history_v1
// reads as "no cut date at all".
//
// MAX IS NOT ONE WINDOW, and that is the fact this whole panel is shaped around. Earliest
// observation per measure, measured on ewyfhrjexfrmcjhqzsmm 2026-09-05:
//   US 2s10s          1976-06-01, 12,562 observations
//   OAT over Bund     2004-11-03,  5,543
//   UK 10y real       2005-01-04,  5,475
//   Gilt over OIS     2016-01-04,  2,695
//   every credit OAS  2023-06-19,     843 (842 for US investment grade)
// Fifty years between the widest and the narrowest, so one Max button lands on a different window
// on every row and the panel has to print the window it actually drew. Every credit measure
// returns the SAME numbers at five years and at Max, because five years already covers all it has.
//
// 5 years is the default because it is the window the collapsed row measures, so opening a row
// shows numbers that agree with the row it came out of before anything is changed.
const MEASURE_WINDOWS = [
  { key: "1y", label: "1 year", years: 1 },
  { key: "3y", label: "3 years", years: 3 },
  { key: "5y", label: "5 years", years: 5 },
  { key: "max", label: "Max", years: null },
] as const;
type MeasureWindowKey = (typeof MEASURE_WINDOWS)[number]["key"];

// Sampling ceiling per series, and the two tenors every history call asks for.
const MAX_POINTS = 400;
// Chosen against the measured observation counts rather than picked round: 1 year is 250 to 262
// trading days and 3 years is 749 to 786, so both windows draw EVERY published observation and
// the disclosure below them stays silent because nothing was dropped. 5 years (1,220 to 1,275)
// and Max are sampled, and say so. The ceiling exists because PostgREST caps a response at 1,000
// rows and Range does not lift it; the path travels as one jsonb array so the cap cannot bite,
// but an unbounded array would still ship 12,562 points to draw a line a few hundred pixels wide.
const MEASURE_MAX_POINTS = 800;
const RV_LOOKBACK_YEARS = 5;
const CURVE_TENORS = [2, 10] as const;

const fmtPct = (v: number | null | undefined) => (v == null ? "" : `${v.toFixed(2)}%`);
const fmtTenor = (t: number) => (t < 1 ? `${Math.round(t * 12)}m` : `${t}y`);
// timeZone is pinned to UTC, deliberately NOT the firm's own zone. These are DATE-ONLY observation
// dates parsed at UTC midnight, so parse-at-Z plus render-at-UTC is host-independent and returns
// exactly the stored calendar date. Pinning the firm's zone would agree only because London is
// never west of UTC, which is luck rather than correctness. See the PINNED_TIMEZONE_LEDGER header
// in tests/contracts/locale-and-money.test.ts, which prefers UTC for exactly this shape of value.
// Without any pin at all, a reader west of GB sees every "as at" a whole day early.
const fmtDate = (iso: string) =>
  new Date(iso + "T00:00:00Z").toLocaleDateString("en-GB", {
    day: "numeric", month: "short", year: "numeric", timeZone: "UTC",
  });

// The tick label on every time axis on this page. ONE Intl call, shared by the two curve
// time-series charts and by the expanded measure chart, so adding those charts does not add a
// third date-rendering site to PINNED_TIMEZONE_LEDGER in tests/contracts/locale-and-money.test.ts.
// Same UTC reasoning as fmtDate: `ms` is always Date.parse of a date-only value at UTC midnight.
//
// yearOnly is for the long windows the Max button reaches. US 2s10s at max spans 50 years, where
// a month-and-year tick is noise: the reader wants the decade, not the month.
const fmtAxisDate = (ms: number, yearOnly = false) =>
  new Date(ms).toLocaleDateString(
    "en-GB",
    yearOnly
      ? { year: "numeric", timeZone: "UTC" }
      : { month: "short", year: "2-digit", timeZone: "UTC" },
  );

// A measure's value in its own unit. Spreads and slopes are basis points and are read whole;
// levels are percent to two places. Shared by the collapsed strip row and the expanded panel, so
// the two cannot round the same number differently: rule 2 of this feature is that the headline in
// the panel equals the Latest column in the row it expanded from, and a second copy of this
// expression is the obvious way that would stop being true.
const fmtMeasure = (v: number, unit: string) =>
  unit === "bp" ? `${Math.round(Number(v))}bp` : `${Number(v).toFixed(2)}%`;

// Thousands separators without Intl. `n.toLocaleString()` would read the host locale for a plain
// count, and this page pins every other formatting decision explicitly.
const fmtCount = (n: number) => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ",");

const DAY_MS = 86400000;
const utcMs = (iso: string) => Date.parse(iso + "T00:00:00Z");

// Axis tick styling, at module scope so the charts inside and outside the page component share one
// definition instead of each carrying its own literal.
const axisTickStyle = { fill: "hsl(var(--muted-foreground))", fontSize: 12 };

type AsOfByCurve = Record<string, string | undefined>;

// Groups the curves on one card by the trading day each was drawn from, newest day first. One
// group means a genuine snapshot; two or more mean the card is an overlay of adjacent days.
function asOfGroups(codes: readonly string[], asOfByCurve: AsOfByCurve): [string, string[]][] {
  const by = new Map<string, string[]>();
  for (const code of codes) {
    const d = asOfByCurve[code];
    if (!d) continue;
    by.set(d, [...(by.get(d) ?? []), CURVE_ATTRIBUTION[code as SourceCode]?.label ?? code]);
  }
  return [...by.entries()].sort(([a], [b]) => b.localeCompare(a));
}

// Joins labels the way a sentence does, without an Oxford comma before "and".
const joinLabels = (labels: string[]) =>
  labels.length <= 1 ? (labels[0] ?? "") : `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;

// ---------------------------------------------------------------------------

export default function PartnerMarkets() {
  // Sign-out clears no persistentCache and the in-memory mirror is never emptied, so an unscoped
  // key seeds the next user on this device from the previous user's value. Every cacheKey below
  // therefore carries the principal.
  const { user } = useAuth();
  const uid = user?.id ?? "anon";

  const [asOfKey, setAsOfKey] = useState<(typeof AS_OF_CHOICES)[number]["key"]>("now");
  const [windowKey, setWindowKey] = useState<(typeof HISTORY_WINDOWS)[number]["key"]>("5y");
  const [showTable, setShowTable] = useState(false);

  const asOfChoice = AS_OF_CHOICES.find((c) => c.key === asOfKey)!;
  const asOfParam = asOfChoice.months === 0 ? null : isoMonthsAgo(asOfChoice.months);

  const catalog = useCachedQuery<CatalogRow[]>({
    queryKey: ["curve-catalog"],
    cacheKey: `partner.${uid}.curve.catalog`,
    queryFn: () => callRpc<CatalogRow>("market_curve_catalog_v1"),
    staleTime: 15 * 60 * 1000,
  });

  const shape = useCachedQuery<ShapeRow[]>({
    queryKey: ["curve-shape", asOfParam],
    cacheKey: `partner.${uid}.curve.shape.${asOfKey}`,
    queryFn: () => callRpc<ShapeRow>("market_curve_shape_v1", { p_curve_codes: null, p_as_of: asOfParam }),
    staleTime: 15 * 60 * 1000,
  });

  const rv = useCachedQuery<RvRow[]>({
    queryKey: ["curve-rv", RV_LOOKBACK_YEARS],
    cacheKey: `partner.${uid}.curve.rv.${RV_LOOKBACK_YEARS}`,
    queryFn: () => callRpc<RvRow>("market_rv_strip_v1", { p_lookback_years: RV_LOOKBACK_YEARS }),
    staleTime: 15 * 60 * 1000,
  });

  const credit = useCachedQuery<RvRow[]>({
    queryKey: ["credit-strip", RV_LOOKBACK_YEARS],
    cacheKey: `partner.${uid}.credit.strip.${RV_LOOKBACK_YEARS}`,
    queryFn: () => callRpc<RvRow>("market_credit_strip_v1", { p_lookback_years: RV_LOOKBACK_YEARS }),
    staleTime: 15 * 60 * 1000,
  });

  // The OAS series only begin in mid-2023, so the credit window is shorter than the curve window
  // and the footnote must say so rather than repeat "five years".
  //
  // ⚠️ THIS USED TO PRINT A DATE, AND THE DATE WAS WRONG. It read credit.data[0].spark_from, which
  // is the first SAMPLED point rather than the first observation: the sparkline keeps 60 points by
  // striding back from the newest, so the oldest one or two observations fall off the end.
  // Measured on the live database 2026-09-05, the credit series truly begin 2023-06-19 while
  // spark_from reported 2023-06-20 for US investment grade and 2023-06-21 for the other six. Two
  // trading days out, stated as though it were the window the percentile was computed over.
  //
  // The strip RPCs do not return the true window start, and widening their `returns table` means a
  // DROP and a client type change, so the date is not printed here at all. The expanded panel calls
  // market_measure_history_v1, which returns the honest window_from and window_to, and the footnote
  // points at it.
  //
  // The claim that the window is SHORTER is measured rather than asserted: the widest credit window
  // against the narrowest government one. Measured the same day, 843 observations against 1,220, so
  // this is true today and goes false on its own the day the OAS history catches up.
  const creditWindowShort = useMemo(() => {
    const c = credit.data ?? [];
    const g = rv.data ?? [];
    if (c.length === 0 || g.length === 0) return false;
    return Math.max(...c.map((r) => Number(r.n_obs))) < Math.min(...g.map((r) => Number(r.n_obs)));
  }, [credit.data, rv.data]);

  // Live IG prices. NOT definer-gated: markets_strip_v1 filters on is_investment_team() in the
  // database, so an Operations or Advisory staff member legitimately receives an empty list.
  const live = useMarketData();

  const freshness = useCachedQuery<FreshnessRow[]>({
    queryKey: ["series-freshness"],
    cacheKey: `partner.${uid}.series.freshness`,
    queryFn: () => callRpc<FreshnessRow>("research_series_freshness_v1"),
    staleTime: 15 * 60 * 1000,
  });

  const health = useMemo(() => {
    const rows = freshness.data ?? [];
    const by = new Map<string, number>();
    for (const r of rows) by.set(r.status, (by.get(r.status) ?? 0) + 1);
    return {
      total: rows.length,
      counts: [...by.entries()].sort((a, b) => b[1] - a[1]),
      problems: rows.filter((r) => r.status !== "ok").slice(0, 8),
    };
  }, [freshness.data]);

  const histFrom = useMemo(() => {
    const w = HISTORY_WINDOWS.find((h) => h.key === windowKey)!;
    const now = new Date();
    return new Date(Date.UTC(now.getUTCFullYear() - w.years, now.getUTCMonth(), now.getUTCDate()))
      .toISOString().slice(0, 10);
  }, [windowKey]);

  // One call per curve, tenors 2y and 10y, serving both the level chart and the slope chart.
  //
  // MAX_POINTS is not decoration. PostgREST enforces db-max-rows=1000 on this project and a Range
  // header cannot lift it, so an unbounded "Since 2005" request would have been cut off mid-series
  // and drawn a line that just stopped, with nothing said. The RPC samples explicitly to at most
  // this many real observations, always keeping the most recent, and the chart footer says so.
  //
  // p_align_curves is what makes these four independent calls comparable. Sampling used to stride by
  // row index per tenor, so each series got its own stride and its own anchor: measured on the five
  // year window, the four 10y lines came back on 875 distinct dates of which NONE carried all four
  // curves, and on the long window the US slope was drawn from 10 days because the 2y and 10y
  // series (12,561 and 16,153 dated observations, so strides of 32 and 41) strode past each other.
  // Passing the same alignment set to every call makes the RPC sample all of them onto one
  // calendar, the dates every named curve actually published, so a point compares four markets on
  // the same trading day. Real observations only, still: the calendar is an intersection of
  // published dates, never a filled grid.
  const history = useCachedQuery<Record<CurveCode, HistRow[]>>({
    queryKey: ["curve-history", histFrom],
    cacheKey: `partner.${uid}.curve.history.${windowKey}`,
    queryFn: async () => {
      const pairs = await Promise.all(
        CURVES.map(async (c) => [
          c.code,
          await callRpc<HistRow>("market_curve_history_v1", {
            p_curve_code: c.code, p_tenor_years: [2, 10], p_from: histFrom, p_max_points: MAX_POINTS,
            p_align_curves: CURVES.map((a) => a.code),
          }),
        ] as const),
      );
      return Object.fromEntries(pairs) as Record<CurveCode, HistRow[]>;
    },
    staleTime: 15 * 60 * 1000,
  });

  // True when the lines are drawn from an evenly spaced subset of trading days rather than every
  // one of them.
  //
  // Counting rows against MAX_POINTS does NOT work: the RPC strides by ceil(n / max), so 5,468
  // gilt observations come back as 391 points, under the 400 ceiling, and a row-count test would
  // have declared the series unsampled and quietly dropped the disclosure. Measure the actual
  // spacing instead. Unsampled daily data has a median gap of 1 day (3 across a weekend); any
  // stride of 2 or more pushes the median to 3 or above.
  const sampled = useMemo(() => {
    for (const rows of Object.values(history.data ?? {}) as HistRow[][]) {
      const dates = rows
        .filter((r) => Number(r.tenor_years) === CURVE_TENORS[1])
        .map((r) => Date.parse(r.observation_date + "T00:00:00Z"))
        .sort((a, b) => a - b);
      if (dates.length < 8) continue;
      const gaps = dates.slice(1).map((d, i) => (d - dates[i]) / 86400000).sort((a, b) => a - b);
      if (gaps[Math.floor(gaps.length / 2)] >= 3) return true;
    }
    return false;
  }, [history.data]);

  // What the shared calendar covers, so the footer can state it rather than let the reader assume
  // every trading day is present. `ahead` is the curves whose own newest observation lands past the
  // last date all four shared, which is the ordinary state of things rather than a fault: measured
  // 2026-09-04, the Bundesbank and the Banque de France had posted 3 September while the Fed and
  // the Bank of England were still at 2 September, so those two points sit alone on the right edge.
  const alignment = useMemo(() => {
    if (!history.data) return null;
    let lastCommon: string | null = null;
    const ahead: { label: string; date: string }[] = [];
    for (const c of CURVES) {
      let own: string | null = null;
      for (const r of history.data[c.code] ?? []) {
        if (r.on_common_grid === false) {
          if (own == null || r.observation_date > own) own = r.observation_date;
        } else if (lastCommon == null || r.observation_date > lastCommon) {
          lastCommon = r.observation_date;
        }
      }
      if (own) ahead.push({ label: c.label, date: own });
    }
    return lastCommon || ahead.length > 0 ? { lastCommon, ahead } : null;
  }, [history.data]);

  // Every publisher the relative-value strip leans on, derived from the rows actually returned so a
  // measure that dropped out of the RPC does not leave its source credited underneath.
  const rvSources = useMemo(() => {
    const codes = new Set<SourceCode>();
    for (const r of rv.data ?? []) {
      for (const code of RV_MEASURE_CURVES[r.measure_key] ?? []) codes.add(code);
    }
    return [...codes].map((code) => CURVE_ATTRIBUTION[code]);
  }, [rv.data]);

  // Attribution for ONE measure, for the panel an expanded row opens. The card-level credit above
  // is the union across every row shown, which under a single expanded chart would credit
  // publishers that chart does not touch. A relative-value row names the curves it subtracts; a
  // credit row is one ICE BofA index reaching us through FRED, so every credit row credits the same
  // pair and the function is constant rather than special-cased inside the strip component.
  const rvSourcesFor = useCallback(
    (measureKey: string) =>
      (RV_MEASURE_CURVES[measureKey] ?? []).map((code) => CURVE_ATTRIBUTION[code]),
    [],
  );
  const creditSourcesFor = useCallback(() => CREDIT_SOURCES, []);

  // Curve shape: union of tenors, one field per curve. Tenors differ between markets (the US has a
  // 1 month bill, the Bund curve starts at 1 year), so gaps are real and left as gaps.
  // One shape query returns every curve; each card slices the codes it plots. The tenor axis is the
  // union of that card's curves, so a tenor one of them does not publish stays a gap.
  const buildShape = useCallback((codes: readonly string[]) => {
    const rows = (shape.data ?? []).filter((r) => codes.includes(r.curve_code));
    const tenors = [...new Set(rows.map((r) => Number(r.tenor_years)))].sort((a, b) => a - b);
    return tenors.map((t) => {
      const point: Record<string, number | null> = { tenor: t };
      for (const code of codes) {
        const hit = rows.find((r) => r.curve_code === code && Number(r.tenor_years) === t);
        point[code] = hit ? Number(hit.value) : null;
      }
      return point;
    });
  }, [shape.data]);

  const shapeSeries = useMemo(() => buildShape(CURVES.map((c) => c.code)), [buildShape]);
  const ukDecompSeries = useMemo(() => buildShape(UK_DECOMP.map((c) => c.code)), [buildShape]);
  const ukOisSeries = useMemo(() => buildShape(UK_VS_OIS.map((c) => c.code)), [buildShape]);

  // Index of each curve's last non-null point, for the direct labels.
  const lastIdx = useMemo(() => {
    const m = {} as Record<CurveCode, number>;
    for (const c of CURVES) {
      m[c.code] = shapeSeries.reduce((acc, p, i) => (p[c.code] != null ? i : acc), -1);
    }
    return m;
  }, [shapeSeries]);

  // market_curve_shape_v1 only returns a curve on a date where EVERY active tenor published, and it
  // looks back 45 days for one. A curve whose ingest has stalled therefore vanishes from the chart
  // silently. Name it instead: absent for a stated reason beats absent with no explanation.
  const missingCurves = useMemo(() => {
    const present = new Set((shape.data ?? []).map((r) => r.curve_code));
    return CURVES.filter((c) => !present.has(c.code)).map((c) => c.label);
  }, [shape.data]);

  const asOfByCurve = useMemo(() => {
    const m: AsOfByCurve = {};
    for (const r of shape.data ?? []) m[r.curve_code] = r.as_of;
    return m;
  }, [shape.data]);

  // Which trading days the country comparison is actually made of. More than one entry means the
  // chart overlays different days, which is the normal case rather than an error, and has to be
  // said where the chart is read rather than left to be inferred from four footer lines.
  const shapeAsOfGroups = useMemo(
    () => asOfGroups(CURVES.map((c) => c.code), asOfByCurve),
    [asOfByCurve],
  );

  // Level and slope share one reshape: date -> { UST: 4.7, ... }.
  const buildTimeSeries = useCallback((tenor: number) => {
    const h = history.data;
    if (!h) return [];
    const byDate = new Map<string, Record<string, number | boolean>>();
    for (const c of CURVES) {
      for (const r of h[c.code] ?? []) {
        if (Number(r.tenor_years) !== tenor) continue;
        const slot = byDate.get(r.observation_date) ?? {};
        slot[c.code] = Number(r.value);
        // Carried through the reshape so the chart can draw an off-grid point as a lone dot rather
        // than joining it to the last shared day. See OffGridDot for why that distinction matters.
        if (r.on_common_grid === false) slot[`${c.code}${OFF_GRID_SUFFIX}`] = true;
        byDate.set(r.observation_date, slot);
      }
    }
    return [...byDate.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([d, v]) => ({ t: Date.parse(d + "T00:00:00Z"), ...v }));
  }, [history.data]);

  const tenYear = useMemo(() => buildTimeSeries(10), [buildTimeSeries]);

  const slope = useMemo(() => {
    const h = history.data;
    if (!h) return [];
    const byDate = new Map<string, Record<string, number>>();
    for (const c of CURVES) {
      const two = new Map<string, number>();
      const ten = new Map<string, number>();
      for (const r of h[c.code] ?? []) {
        (Number(r.tenor_years) === 2 ? two : ten).set(r.observation_date, Number(r.value));
      }
      for (const [d, t10] of ten) {
        const t2 = two.get(d);
        if (t2 == null) continue; // no 2y that day, so no spread. Never assumed.
        const slot = byDate.get(d) ?? {};
        slot[c.code] = (t10 - t2) * 100; // basis points
        byDate.set(d, slot);
      }
    }
    return [...byDate.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([d, v]) => ({ t: Date.parse(d + "T00:00:00Z"), ...v }));
  }, [history.data]);

  // isPlaceholderData, not just isLoading. useCachedQuery seeds placeholderData with the FUNCTION
  // form `(prev) => prev ?? cached`, which is how React Query implements keepPreviousData: on a key
  // change the PREVIOUS selection's rows come back as the new key's placeholder, status is
  // "success" and isLoading is FALSE. Both of these keys are driven by a user control (asOfKey on
  // the shape card, windowKey on the two history cards), so keying the skeleton on isLoading alone
  // paints the old selection's chart under the newly lit button, presented as settled.
  //
  // Measured on the sibling read during this work: switching the relative-value panel to 1 year
  // left the FIVE year percentile (67) on screen under a lit "1 year" button for a full round trip,
  // where the true figure was 18.8. Same shape, same cause. See MeasureHistoryPanel, which guards
  // the same hazard by checking the RPC's own requested_years echo.
  //
  // isPlaceholderData rather than isFetching deliberately: a background refetch of the SAME key
  // still has real data for that key, so this does not flash a skeleton every time the 15 minute
  // staleTime lapses. It fires only when what is on screen is not for the selection on screen.
  const loading =
    shape.isLoading || history.isLoading ||
    shape.isPlaceholderData || history.isPlaceholderData;
  const failed = (shape.error || history.error) && !shape.data && !history.data;
  const offlineHint = shape.isFromCache || history.isFromCache;

  const timeAxis = {
    dataKey: "t" as const,
    type: "number" as const,
    scale: "time" as const,
    domain: ["dataMin", "dataMax"] as [string, string],
    // fmtAxisDate carries the UTC pin and the reasoning for it. It used to be inline here; it was
    // lifted out when the expanded measure chart needed the same tick, so that this page keeps two
    // date-rendering sites rather than one per chart.
    tickFormatter: (v: number) => fmtAxisDate(v),
  };

  const axisTick = axisTickStyle;

  return (
    <div className="space-y-8">
      <header className="space-y-2">
        <h1 className="text-2xl font-bold">Markets</h1>
        <p className="text-muted-foreground max-w-3xl">
          Government yield curves for the four markets that set the discount rate on almost
          everything we own. Published data only, refreshed each morning.
        </p>
        {offlineHint && (
          <Badge variant="outline" className="gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5" />
            Showing saved data
          </Badge>
        )}
      </header>

      <RelativeValueStrip
        rows={rv.data ?? []}
        loading={rv.isLoading}
        uid={uid}
        title="Relative value"
        description="Where each government spread sits against its own last five years. Percentile is the share of that window at or below today, so 100 is the widest it has been and 0 the tightest. Spreads are computed only on days both legs published. Select a row to chart its history over a longer or shorter window."
        footnote="Measurements of published data, not recommendations, and not a view on what any of it is worth. The window is a rolling calendar window, so the range moves as history rolls off. Median is marked as a faint tick on each track. A spread between two markets is our own subtraction of two publishers' curves: neither publisher publishes the difference, and neither is a source for it. The credits below are the publishers the rows shown actually draw on."
        sources={rvSources}
        // Per row rather than per card, so an expanded panel credits the publishers THAT row leans
        // on instead of every publisher the card as a whole touches.
        sourcesFor={rvSourcesFor}
      />

      <RelativeValueStrip
        rows={credit.data ?? []}
        loading={credit.isLoading}
        uid={uid}
        title="Credit spreads"
        description="Option-adjusted spreads by credit quality, in basis points. Same reading as above: percentile is the share of the window at or below today, so a low number means spreads are tight against their own history. Select a row to chart its history."
        footnote={
          creditWindowShort
            ? "These series begin in mid-2023, so the window here is shorter than the government-curve window above: a five year request resolves to less than five years of real history. Expand a row for the exact first and last observation it covers. Ordered by credit quality, not by size of move."
            : "Ordered by credit quality, not by size of move."
        }
        sources={CREDIT_SOURCES}
        sourcesFor={creditSourcesFor}
      />

      {!loading && missingCurves.length > 0 && (
        <Card className="border-amber-500/40">
          <CardContent className="flex items-start gap-2 py-4 text-sm">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <span>
              <span className="font-medium">{missingCurves.join(", ")}</span>{" "}
              {missingCurves.length === 1 ? "is" : "are"} not shown. No recent day was found on which
              every maturity published, so the curve is omitted rather than drawn with gaps or
              assembled from different dates.
            </span>
          </CardContent>
        </Card>
      )}

      {failed && (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Curve data could not be loaded. Nothing is shown rather than a partial curve.
          </CardContent>
        </Card>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* 1. Curve shape                                                      */}
      {/* ------------------------------------------------------------------ */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <CardTitle>Yield curve shape</CardTitle>
              <CardDescription>
                Spot yield against maturity. Each curve is drawn from a single trading day, never
                assembled across dates, and that day is each market's own most recent complete one.
              </CardDescription>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {AS_OF_CHOICES.map((c) => (
                <Button
                  key={c.key}
                  size="sm"
                  variant={c.key === asOfKey ? "default" : "outline"}
                  onClick={() => setAsOfKey(c.key)}
                >
                  {c.label}
                </Button>
              ))}
              <Button size="sm" variant="outline" onClick={() => setShowTable((v) => !v)}>
                {showTable ? <LineChartIcon className="h-4 w-4" /> : <TableIcon className="h-4 w-4" />}
                <span className="ml-1.5">{showTable ? "Chart" : "Table"}</span>
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading && <div className="h-[420px] animate-pulse rounded-md bg-muted" />}

          {/* Above both the chart and the table, because the table overlays the same mixed dates
              in its columns. */}
          {!loading && <AsOfBanner groups={shapeAsOfGroups} />}

          {!loading && !showTable && shapeSeries.length > 0 && (
            <ChartContainer config={chartConfig} className="h-[420px] w-full aspect-auto">
              <LineChart data={shapeSeries} margin={{ top: 8, right: 56, bottom: 8, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="tenor"
                  type="number"
                  scale="sqrt"
                  domain={[0, 40]}
                  ticks={[0.25, 1, 2, 5, 10, 20, 30, 40]}
                  tickFormatter={fmtTenor}
                  axisLine={false}
                  tickLine={false}
                  tick={axisTick}
                  label={{ value: "Maturity", position: "insideBottom", offset: -4, fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                />
                <YAxis
                  domain={["auto", "auto"]}
                  tickFormatter={(v: number) => `${v.toFixed(1)}%`}
                  axisLine={false}
                  tickLine={false}
                  tick={axisTick}
                  width={56}
                />
                <ChartTooltip
                  content={<ChartTooltipContent
                    labelFormatter={(v) => `Maturity ${fmtTenor(Number(v))}`}
                    // The date rides along with the value whenever the card spans more than one
                    // day. A reader comparing four numbers at 10y must not have to remember which
                    // of them is a day behind.
                    formatter={(value, name) => [
                      ` ${fmtPct(Number(value))}`,
                      seriesLabelWithDate(
                        String(name), chartConfig[name as string]?.label, shapeAsOfGroups, asOfByCurve,
                      ),
                    ]}
                  />}
                />
                <ChartLegend verticalAlign="bottom" content={<ChartLegendContent />} />
                {CURVES.map((c) => (
                  <Line
                    key={c.code}
                    type="monotone"
                    dataKey={c.code}
                    name={c.code}
                    stroke={`var(--color-${c.code})`}
                    strokeWidth={2}
                    dot={{ r: 3, strokeWidth: 0, fill: `var(--color-${c.code})` }}
                    activeDot={{ r: 5 }}
                    connectNulls
                    isAnimationActive={false}
                  >
                    {/* Direct label at this curve's own longest published tenor, so identity is
                        never carried by colour alone. Curves end at different maturities (the US
                        stops at 30y, the gilt curve runs to 40y), so the anchor is per curve. */}
                    <LabelList dataKey={c.code} content={<EndLabel text={c.short} at={lastIdx[c.code]} />} />
                  </Line>
                ))}
              </LineChart>
            </ChartContainer>
          )}

          {!loading && showTable && (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Maturity</TableHead>
                    {CURVES.map((c) => <TableHead key={c.code} className="text-right">{c.label}</TableHead>)}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {shapeSeries.map((row) => (
                    <TableRow key={String(row.tenor)}>
                      <TableCell className="font-medium">{fmtTenor(Number(row.tenor))}</TableCell>
                      {CURVES.map((c) => (
                        <TableCell key={c.code} className="text-right tabular-nums">
                          {row[c.code] == null ? <span className="text-muted-foreground">not published</span> : fmtPct(row[c.code] as number)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          <SourceFooter asOfByCurve={asOfByCurve} />
        </CardContent>
      </Card>

      {/* ------------------------------------------------------------------ */}
      {/* 1b. UK detail: what the nominal curve is made of, and what it pays  */}
      {/*     over the expected path of Bank Rate                             */}
      {/* ------------------------------------------------------------------ */}
      <div className="grid gap-8 lg:grid-cols-2">
        <CurveShapeCard
          title="UK nominal, real and implied inflation"
          description="What the gilt curve is made of. Real yields come from index-linked gilts; implied inflation is the difference between the two."
          defs={UK_DECOMP}
          series={ukDecompSeries}
          asOfByCurve={asOfByCurve}
          footnote="Bank of England, Open Government Licence. The Bank publishes the real and inflation curves from 2.5 years (too few short-dated index-linked gilts trade to fit a curve below that) and the tenors registered here start at 3, so those two lines begin a step later than the nominal one. Nothing below is estimated. The three curves are parsed from three separate workbooks in the Bank's daily zip and reconcile exactly, real plus implied inflation equalling the nominal yield with no residual at six decimal places of a basis point: measured across all ten shared tenors on 25 August and 2 September 2026, and at ten years on every one of the 1,263 trading days since September 2021. Compounding them instead of adding them leaves the cross term of the two rates, 2.2bp at 3 years, 6.3bp at 10 years and 8.9bp at 25 years on 2 September 2026, which is arithmetic rather than a disagreement between the curves."
        />
        <CurveShapeCard
          title="Gilt yields versus SONIA OIS"
          description="The gilt curve against the market-implied path of Bank Rate. The gap between them is the gilt asset-swap spread."
          defs={UK_VS_OIS}
          series={ukOisSeries}
          asOfByCurve={asOfByCurve}
          footnote="Bank of England, Open Government Licence. OIS is published from one month out to 25 years, so the 30 and 40 year gilt points have no OIS counterpart and the line stops. The gap does not widen monotonically: on 25 August 2026 it was 12bp at six months, narrowed to 5bp at two years, and then widened out to 78bp at 25 years."
        />
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* 2. Ten year level through time                                      */}
      {/* ------------------------------------------------------------------ */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <CardTitle>Ten year yields</CardTitle>
              <CardDescription>
                The long end of each market, on the trading days all four of them share.
              </CardDescription>
            </div>
            <div className="flex gap-1.5">
              {HISTORY_WINDOWS.map((w) => (
                <Button
                  key={w.key}
                  size="sm"
                  variant={w.key === windowKey ? "default" : "outline"}
                  onClick={() => setWindowKey(w.key)}
                >
                  {w.label}
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading && <div className="h-[340px] animate-pulse rounded-md bg-muted" />}
          {!loading && tenYear.length > 0 && (
            <ChartContainer config={chartConfig} className="h-[340px] w-full aspect-auto">
              <LineChart data={tenYear} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                <XAxis {...timeAxis} axisLine={false} tickLine={false} tick={axisTick} minTickGap={40} />
                <YAxis
                  // Bund and euro area AAA yields were genuinely negative through 2021, so the
                  // axis must cross zero. Padded to the nearest half point rather than left on
                  // recharts' default rounding, which stretched the scale to -2% over empty space.
                  domain={[
                    (min: number) => Math.floor((min - 0.25) * 2) / 2,
                    (max: number) => Math.ceil((max + 0.25) * 2) / 2,
                  ]}
                  tickFormatter={(v: number) => `${v.toFixed(1)}%`}
                  axisLine={false} tickLine={false} tick={axisTick} width={56}
                />
                <ChartTooltip
                  content={<ChartTooltipContent
                    labelFormatter={(_l, p) => fmtDate(new Date(Number(p?.[0]?.payload?.t)).toISOString().slice(0, 10))}
                    formatter={(value, name) => [` ${fmtPct(Number(value))}`, chartConfig[name as string]?.label ?? name]}
                  />}
                />
                <ChartLegend verticalAlign="bottom" content={<ChartLegendContent />} />
                {CURVES.map((c) => (
                  <Line key={c.code} type="linear" dataKey={c.code} name={c.code}
                    stroke={`var(--color-${c.code})`} strokeWidth={2}
                    dot={<OffGridDot code={c.code} fill={`var(--color-${c.code})`} />}
                    activeDot={{ r: 4 }} connectNulls={false} isAnimationActive={false} />
                ))}
              </LineChart>
            </ChartContainer>
          )}
          <SourceFooter asOfByCurve={asOfByCurve} sampled={sampled} alignment={alignment} />
        </CardContent>
      </Card>

      {/* ------------------------------------------------------------------ */}
      {/* 3. Slope                                                            */}
      {/* ------------------------------------------------------------------ */}
      <Card>
        <CardHeader>
          <CardTitle>Curve slope, two year to ten year</CardTitle>
          <CardDescription>
            Ten year minus two year, in basis points. Below the zero line the curve is inverted.
            Only days on which both tenors were published are plotted.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading && <div className="h-[300px] animate-pulse rounded-md bg-muted" />}
          {!loading && slope.length > 0 && (
            <ChartContainer config={chartConfig} className="h-[300px] w-full aspect-auto">
              <LineChart data={slope} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                <XAxis {...timeAxis} axisLine={false} tickLine={false} tick={axisTick} minTickGap={40} />
                <YAxis
                  tickFormatter={(v: number) => `${Math.round(v)}`}
                  axisLine={false} tickLine={false} tick={axisTick} width={56}
                  label={{ value: "bp", angle: -90, position: "insideLeft", fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                />
                <ReferenceLine y={0} stroke="hsl(var(--foreground))" strokeOpacity={0.45} strokeDasharray="4 4" />
                <ChartTooltip
                  content={<ChartTooltipContent
                    labelFormatter={(_l, p) => fmtDate(new Date(Number(p?.[0]?.payload?.t)).toISOString().slice(0, 10))}
                    formatter={(value, name) => [` ${Math.round(Number(value))}bp`, chartConfig[name as string]?.label ?? name]}
                  />}
                />
                <ChartLegend verticalAlign="bottom" content={<ChartLegendContent />} />
                {CURVES.map((c) => (
                  <Line key={c.code} type="linear" dataKey={c.code} name={c.code}
                    stroke={`var(--color-${c.code})`} strokeWidth={2}
                    dot={<OffGridDot code={c.code} fill={`var(--color-${c.code})`} />}
                    activeDot={{ r: 4 }} connectNulls={false} isAnimationActive={false} />
                ))}
              </LineChart>
            </ChartContainer>
          )}
          <SourceFooter asOfByCurve={asOfByCurve} sampled={sampled} alignment={alignment} />
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        Two methodologies sit on these charts. The US and French curves are constant maturity
        (par-type) rates; the UK and German curves are zero-coupon spot rates. At ten years the
        difference is a few basis points, so levels are comparable, but they are not the same
        measure. The euro area AAA curve is also ingested and available on request.
      </p>
      {/* ------------------------------------------------------------------ */}
      {/* Internal only: live prices and data health                          */}
      {/* ------------------------------------------------------------------ */}
      <div className="grid gap-8 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Live prices</CardTitle>
            <CardDescription>
              IG instrument watch list, updating through the trading day. Proprietary feed, so this
              panel is investment team only.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {live.loading && <div className="h-[320px] animate-pulse rounded-md bg-muted" />}
            {!live.loading && live.rows.length === 0 && (
              <div className="flex items-start gap-2 py-6 text-sm text-muted-foreground">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  No prices returned. This feed is restricted to the investment team in the database
                  (<code className="text-xs">markets_strip_v1</code> filters on{" "}
                  <code className="text-xs">is_investment_team()</code>), so an empty list here means
                  your account is not on that team rather than that the feed is down. The curve and
                  spread panels above are unaffected.
                </span>
              </div>
            )}
            {!live.loading && live.rows.length > 0 && (
              <div className="max-h-[320px] space-y-1 overflow-y-auto pr-1">
                {live.rows.map((row) => (
                  <MarketStrip key={row.ticker} row={row} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Data health</CardTitle>
            <CardDescription>
              Every active series graded against its own observation cadence. A series is flagged on
              its own spacing, not on a frequency label.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {freshness.isLoading && <div className="h-[320px] animate-pulse rounded-md bg-muted" />}
            {!freshness.isLoading && health.total > 0 && (
              <>
                <div className="flex flex-wrap gap-2">
                  {health.counts.map(([status, n]) => (
                    <Badge key={status} variant={status === "ok" ? "secondary" : "outline"}
                      className={status === "ok" ? "" : "border-amber-500/50 text-amber-700 dark:text-amber-400"}>
                      {n} {status.replace(/_/g, " ")}
                    </Badge>
                  ))}
                </div>
                {health.problems.length > 0 && (
                  <div className="mt-4 overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Series</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Last observation</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {health.problems.map((r) => (
                          <TableRow key={r.series_id}>
                            <TableCell className="font-mono text-xs">{r.series_id}</TableCell>
                            <TableCell className="text-xs">{r.status.replace(/_/g, " ")}</TableCell>
                            <TableCell className="text-right text-xs tabular-nums whitespace-nowrap">
                              {r.last_observation_date ? fmtDate(r.last_observation_date) : "never"}
                              {r.days_since_obs != null && (
                                <span className="text-muted-foreground"> ({r.days_since_obs}d)</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
                <p className="mt-4 border-t pt-3 text-xs text-muted-foreground">
                  Showing the first {health.problems.length} of {health.total - (health.counts.find(([s]) => s === "ok")?.[1] ?? 0)} series
                  needing attention. Full detail is in docs/yield-curves/DATA-HEALTH.md.
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {catalog.data && catalog.data.some((c) => !c.active) && (
        <p className="text-xs text-muted-foreground">
          One registered curve is not currently ingesting. Its tenors are omitted rather than
          estimated.
        </p>
      )}
    </div>
  );
}

// Draws a point that sits PAST the end of the shared comparison calendar, and nothing else.
//
// WHY THESE POINTS MUST NOT BE JOINED TO THE LINE. The history charts plot only dates every curve
// published, so a curve that printed ahead of the others carries its newest observation as an
// isolated point on the right edge. With connectNulls that point is joined to the last shared day
// by a straight segment. On the ordinary one day lag that segment is harmless. When a connector
// stalls it is not: the shared calendar loses every day of the stall for EVERY curve, and the
// segment is then drawn straight across however many days of real, published, dropped observations
// the stall lasted. A smooth line over discarded data is visually indistinguishable from
// interpolation, on the right-hand edge where the eye goes first, and this estate has had a nine
// day and an eleven day stall in this quarter alone.
//
// So the line stops at the last shared day and the off-grid print is drawn as a lone dot. A gap
// reads as a gap. The footer states which day the shared calendar ends on and which curves sit
// past it, so the dot is explained rather than merely visible.
function OffGridDot({ code, fill, cx, cy, payload }: {
  code: string; fill: string; cx?: number; cy?: number; payload?: Record<string, unknown>;
}) {
  if (cx == null || cy == null || !payload?.[`${code}${OFF_GRID_SUFFIX}`]) return null;
  return <circle cx={cx} cy={cy} r={3} fill={fill} />;
}

// Renders one direct label, at the index the caller nominates. Recharts hands a LabelList
// `content` component the per-point geometry, which `formatter` does not expose.
function EndLabel({ text, at, x, y, index, value }: {
  text: string; at: number; x?: number; y?: number; index?: number; value?: number | null;
}) {
  if (index !== at || value == null || x == null || y == null) return null;
  return (
    <text x={x + 7} y={y} dy={4} fontSize={11} fontWeight={500} fill="hsl(var(--muted-foreground))">
      {text}
    </text>
  );
}

// States which trading day, or days, a curve-shape card is drawn from.
//
// Markets keep different holiday calendars and publish at different lags, so the most recent
// COMPLETE curve is routinely a different day in each one. Measured on the live database
// 2026-09-04: the Bundesbank and the Banque de France had posted 3 September while the Fed and the
// Bank of England were still at 2 September, so the country chart was overlaying two trading days
// as though they were one instant. Over the past year the four curves published on 259 distinct
// dates and shared only 242 of them.
//
// The per-curve dates were already in the source footer, which is not the same as saying they
// differ: four dates listed under a chart read as provenance, not as a warning. Nothing is aligned
// or carried forward to make them agree, because that would invent a reading no publisher printed.
// The mismatch is named instead, beside the chart it affects.
function AsOfBanner({ groups }: { groups: [string, string[]][] }) {
  if (groups.length === 0) return null;

  if (groups.length === 1) {
    return (
      <p className="mb-3 text-xs text-muted-foreground">
        All curves as at {fmtDate(groups[0][0])}, one trading day.
      </p>
    );
  }

  return (
    <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-500/40 px-3 py-2 text-xs text-muted-foreground">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
      <span>
        <span className="font-medium text-foreground">
          Not one trading day: this chart spans {groups.length} dates.
        </span>{" "}
        {groups.map(([d, labels]) => `${joinLabels(labels)} at ${fmtDate(d)}`).join("; ")}. Each
        market publishes on its own calendar, so the newest complete curve is not the same day
        everywhere. Nothing has been moved onto a common date to hide it.
      </span>
    </div>
  );
}

// Appends the trading day to a tooltip's series label when the card spans more than one, so the
// mismatch is also visible at the moment a value is read rather than only above the chart.
function seriesLabelWithDate(
  code: string, label: ReactNode, groups: [string, string[]][], asOfByCurve: AsOfByCurve,
): ReactNode {
  const asOf = asOfByCurve[code];
  if (groups.length <= 1 || !asOf) return label ?? code;
  return `${typeof label === "string" ? label : code} (${fmtDate(asOf)})`;
}

// Relative value strip.
//
// This reports WHERE THINGS SIT. It is not a recommendation and must not become one: every column
// is a measurement of published data (a level, its percentile within its own five-year window, and
// the path it took). An adviser reads the numbers and reaches their own conclusion.
//
// Deliberately NOT colour-coded by percentile. A red/green ramp would assert that a wide spread is
// bad and a tight one good, which is a view, not a fact: whether 98th-percentile OAT-Bund is an
// opportunity or a warning depends on what you own. The track is neutral and the number carries the
// information.
// EXPANSION. Every row opens an inline panel under it holding the same measure's history over a
// window the reader chooses. One row at a time: each panel is a full chart plus its own statistics,
// and several open at once turns a comparison table into a scroll. The measure's own numbers are
// refetched for the chosen window rather than carried down from the row, for the reason set out
// above MeasureHistoryPanel.
//
// Collapsed, a row is what it always was, plus the disclosure chevron that says it opens. That
// chevron is the one deliberate change to the collapsed appearance: an expandable row with no
// affordance is an expandable row nobody finds.
function RelativeValueStrip({
  rows, loading, uid, title, description, footnote, sources, sourcesFor,
}: {
  rows: RvRow[]; loading: boolean; uid: string;
  title: string; description: string; footnote: string;
  sources: { label: string; source: string }[];
  sourcesFor: (measureKey: string) => { label: string; source: string }[];
}) {
  const [openKey, setOpenKey] = useState<string | null>(null);
  // Held here rather than inside the panel so the window survives moving between rows in this card,
  // and so the two cards keep independent selections. It starts at five years because that is the
  // window the collapsed row measures.
  const [windowKey, setWindowKey] = useState<MeasureWindowKey>("5y");
  // Unique per mounted strip, so the two cards on this page cannot mint the same aria-controls id.
  const panelIdBase = useId();

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        {loading && <div className="h-[360px] animate-pulse rounded-md bg-muted" />}
        {!loading && rows.length === 0 && (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Relative value measures are unavailable.
          </p>
        )}
        {!loading && rows.length > 0 && (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Measure</TableHead>
                  <TableHead className="text-right">Latest</TableHead>
                  <TableHead className="text-right">Percentile</TableHead>
                  <TableHead className="min-w-[190px]">Position in range</TableHead>
                  <TableHead className="min-w-[130px]">Path</TableHead>
                  <TableHead className="text-right">As at</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => {
                  const cur = Number(r.current_value);
                  const lo = Number(r.window_min);
                  const hi = Number(r.window_max);
                  const med = Number(r.window_median);
                  const pts = sparklinePoints((r.spark ?? []).map(Number), 120, 26);
                  const isOpen = openKey === r.measure_key;
                  const panelId = `${panelIdBase}-${r.measure_key}`;
                  const toggle = () => setOpenKey(isOpen ? null : r.measure_key);
                  return (
                    <Fragment key={r.measure_key}>
                      <TableRow
                        // The whole row is the click target, because that is what a reader reaches
                        // for, but the ACCESSIBLE control is the real button in the first cell. A
                        // <tr role="button"> would trade the table's own row semantics away for a
                        // click target, and a screen reader would stop announcing which column each
                        // number belongs to. The button carries aria-expanded and aria-controls, is
                        // in the tab order for free, and stops propagation so a click on it is not
                        // counted twice against this same row.
                        onClick={toggle}
                        className="cursor-pointer"
                        data-state={isOpen ? "open" : "closed"}
                      >
                        <TableCell className="font-medium">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); toggle(); }}
                            aria-expanded={isOpen}
                            aria-controls={panelId}
                            className="flex items-center gap-1.5 text-left hover:underline"
                          >
                            <ChevronRight
                              aria-hidden="true"
                              className={`h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform ${isOpen ? "rotate-90" : ""}`}
                            />
                            {r.label}
                          </button>
                        </TableCell>
                        <TableCell className="text-right tabular-nums font-semibold">
                          {fmtMeasure(cur, r.unit)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {Number(r.pctile).toFixed(0)}
                          <span className="text-muted-foreground text-xs">/100</span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="tabular-nums text-xs text-muted-foreground w-14 text-right">
                              {fmtMeasure(lo, r.unit)}
                            </span>
                            <div className="relative h-1.5 flex-1 rounded-full bg-muted" role="img"
                              // NOT "a five year range". Both strips ASK for five years, but the
                              // seven credit measures only begin 2023-06-19 and so measure about
                              // 3.2 years: n_obs 843 against 1,220 on the narrowest government row,
                              // measured 2026-09-05. Naming five years here stated a window seven of
                              // the seventeen rows do not have, to the one reader who cannot see the
                              // track. The exact first and last observation is in the expanded panel,
                              // which gets it from the RPC rather than from a label.
                              aria-label={`${fmtMeasure(cur, r.unit)}, ${Number(r.pctile).toFixed(0)} of 100 within a measured range of ${fmtMeasure(lo, r.unit)} to ${fmtMeasure(hi, r.unit)}. Select the row for the exact window.`}>
                              {/* median tick, so the midpoint of the range is visible */}
                              <span className="absolute top-1/2 h-2.5 w-px -translate-y-1/2 bg-muted-foreground/50"
                                style={{ left: `${rangeFraction(med, lo, hi) * 100}%` }} />
                              <span className="absolute top-1/2 h-3 w-1 -translate-x-1/2 -translate-y-1/2 rounded-sm bg-foreground"
                                style={{ left: `${rangeFraction(cur, lo, hi) * 100}%` }} />
                            </div>
                            <span className="tabular-nums text-xs text-muted-foreground w-14">
                              {fmtMeasure(hi, r.unit)}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {pts ? (
                            <svg width={120} height={26} viewBox={`0 0 120 26`} className="overflow-visible"
                              role="img" aria-label={`Path from ${r.spark_from} to ${r.spark_to}`}>
                              <polyline points={pts} fill="none" strokeWidth={1.5}
                                stroke="hsl(var(--muted-foreground))" strokeLinejoin="round" strokeLinecap="round" />
                            </svg>
                          ) : (
                            <span className="text-xs text-muted-foreground">not enough history</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right text-xs text-muted-foreground whitespace-nowrap">
                          {fmtDate(r.as_of)}
                        </TableCell>
                      </TableRow>
                      {isOpen && (
                        <TableRow className="hover:bg-transparent">
                          <TableCell colSpan={6} id={panelId} className="bg-muted/30 p-0">
                            <MeasureHistoryPanel
                              measureKey={r.measure_key}
                              label={r.label}
                              uid={uid}
                              windowKey={windowKey}
                              onWindowChange={setWindowKey}
                              sources={sourcesFor(r.measure_key)}
                              rowValue={cur}
                              rowUnit={r.unit}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
        <div className="mt-4 space-y-1 border-t pt-3 text-xs text-muted-foreground">
          <p className="pb-1">{footnote}</p>
          <SourceLines lines={sources} />
        </div>
      </CardContent>
    </Card>
  );
}

// Palette for the single line the expanded chart draws.
//
// NOT hsl(var(--primary)). That token is CBAM green (#004742) in light mode and near-white
// (210 40% 98%) in dark, so a --primary series is a pale line on a pale ground in one mode and
// invisible against the dark card in the other. Explicit steps per mode, like every other series on
// this page.
//
// Neutral ink rather than a hue, and deliberately. The strip refuses to colour-code by percentile
// because a red/green ramp asserts that a wide spread is bad, which is a view rather than a fact;
// a single-series chart has nothing to distinguish, so a hue here would be decoration inviting the
// same reading. This is the ink the range track already uses for the current marker. Contrast
// against the grounds each step actually renders on: #334155 on white is 9.8:1, #CBD5E1 on the dark
// card ground (222.2 84% 4.9%) is 13.5:1, both far past the 3:1 floor for a graphical object.
const MEASURE_CHART_CONFIG: ChartConfig = {
  v: { label: "Value", theme: { light: "#334155", dark: "#CBD5E1" } },
};

// The expanded history of ONE measure, over a window the reader chooses.
//
// WHY EVERY NUMBER HERE IS REFETCHED RATHER THAN INHERITED FROM THE ROW. A percentile, a minimum, a
// median and a maximum are properties OF THE WINDOW they were computed over. Carrying the row's
// five year percentile onto a one year chart would put a straightforwardly wrong number on screen,
// and not by a rounding margin. Measured on ewyfhrjexfrmcjhqzsmm 2026-09-05, all on the same
// unchanged current value:
//   US 2s10s, 43bp          18.8 at 1y, 59.4 at 3y, 66.7 at 5y, 35.6 at Max
//   Gilt over SONIA, 49.53  32.9,       52.8,       71.7,       86.6
//   US investment grade, 81 78.2,       36.8,       34.3,       34.3
// The whole panel is therefore ONE call scoped to the window on screen, and nothing in it is
// derived from the collapsed row.
//
// WHAT IS WINDOW-INDEPENDENT is the current value itself, which is the same observation whatever
// window contains it. That is the invariant tying the panel to the row it opened from, and it is
// checked at render rather than asserted: see `disagrees` below.
//
// WHAT MAX MEANS HERE. Max is "no cut date", which is a different span on every row. This panel
// always prints the true first and last observation it drew, so the reader never has to assume the
// button label was honoured.
function MeasureHistoryPanel({
  measureKey, label, uid, windowKey, onWindowChange, sources, rowValue, rowUnit,
}: {
  measureKey: string;
  label: string;
  uid: string;
  windowKey: MeasureWindowKey;
  onWindowChange: (k: MeasureWindowKey) => void;
  sources: { label: string; source: string }[];
  rowValue: number;
  rowUnit: string;
}) {
  const choice = MEASURE_WINDOWS.find((w) => w.key === windowKey)!;

  // Cache key scoped per PRINCIPAL, per MEASURE and per WINDOW. All three are load-bearing and each
  // one alone is a way to serve one row's data as another's: sign-out clears no persistentCache and
  // the in-memory mirror is never emptied, so an unscoped key seeds the next user on this device;
  // without the measure the second row opened would paint the first row's path; and without the
  // window a one year chart would be drawn from five year points, with a one year caption over it.
  const hist = useCachedQuery<MeasureHistoryRow[]>({
    queryKey: ["measure-history", measureKey, choice.years],
    cacheKey: `partner.${uid}.measure.history.${measureKey}.${windowKey}`,
    queryFn: () =>
      callRpc<MeasureHistoryRow>("market_measure_history_v1", {
        p_measure_key: measureKey,
        p_lookback_years: choice.years,
        p_max_points: MEASURE_MAX_POINTS,
      }),
    staleTime: 15 * 60 * 1000,
  });

  // ⚠️ THE ROW IS ACCEPTED ONLY IF IT IS THE WINDOW ON SCREEN, and this guard is not defensive
  // decoration. useCachedQuery seeds placeholderData with the FUNCTION form `(prev) => prev ??
  // cached`, which is precisely how React Query v5 implements keepPreviousData: when the key
  // changes, the previous key's data is handed back as the new key's placeholder, status is
  // "success" and isLoading is FALSE. Measured in measure-history-expansion.test.tsx: click
  // 1 year on an open five year panel and, until the fetch resolves, the panel rendered
  // "Percentile in this window 67/100", "-108bp to 129bp" and "1,248 observations, 7 Sept 2021 to 3
  // Sept 2026" with the 1 year button already lit and no saved-data badge. That is the five year
  // measurement presented as the one year one, which is the single thing this feature must not do.
  //
  // The test is the RPC's own echo of what it was asked for rather than anything derived here:
  // requested_years is null for Max and the requested year count otherwise, and measure_key guards
  // the same carry-over across rows. A row failing it is not rendered at all, and the panel falls
  // back to the pending state below rather than drawing a stale one.
  const served = hist.data?.[0] ?? null;
  const row =
    served != null &&
    served.measure_key === measureKey &&
    (served.requested_years ?? null) === (choice.years ?? null)
      ? served
      : null;

  // isFetching, NOT isLoading. isLoading is false while placeholder data is being served, so keying
  // the skeleton off it would leave the guard above rendering "No observations in this window"
  // during a perfectly normal window switch, asserting an absence that has not been measured yet.
  const pending = hist.isFetching;

  const series = useMemo(
    () => (row?.points ?? []).map((p) => ({ t: utcMs(p.observation_date), v: Number(p.value) })),
    [row],
  );

  const unit = row?.unit ?? rowUnit;
  // Span of the window actually drawn, used only to choose the tick format.
  const spanYears = row ? (utcMs(row.window_to) - utcMs(row.window_from)) / (365.25 * DAY_MS) : 0;

  // How far short of the requested cut the data actually starts. requested_from is null for Max.
  //
  // The threshold is not fussiness. A cut date is a piece of calendar arithmetic, not a publishing
  // day: measured 2026-09-05, a five year request on US 2s10s cut at 2021-09-06 and the window
  // opens 2021-09-07, because 6 September 2021 was Labor Day. Announcing a one day shortfall on
  // every row would bury the case that matters, which is a measure that does not go back anywhere
  // near as far as the button says. A month is comfortably clear of any holiday run.
  const shortfallDays = row?.requested_from
    ? Math.round((utcMs(row.window_from) - utcMs(row.requested_from)) / DAY_MS)
    : 0;
  const materiallyShort = shortfallDays > 31;

  // Rule 2, checked rather than claimed. The current value is the same observation at every window,
  // so the headline here must render identically to the Latest column of the row this panel opened
  // from. It has never differed in measurement (all 17 measures agree at every window on
  // 2026-09-05), and it cannot differ while both sides read market_measure_defs_v1 through
  // fmtMeasure. It is compared anyway, because the way it WOULD start differing is a defs edit that
  // splits the two paths, and that failure is silent everywhere else.
  const disagrees =
    row != null && fmtMeasure(Number(row.current_value), unit) !== fmtMeasure(rowValue, rowUnit);

  const showZero = row != null && Number(row.window_min) < 0 && Number(row.window_max) > 0;

  return (
    <div className="space-y-4 px-4 py-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm font-medium">{label}</div>
        <div className="flex flex-wrap gap-1.5">
          {MEASURE_WINDOWS.map((w) => (
            <Button
              key={w.key}
              size="sm"
              variant={w.key === windowKey ? "default" : "outline"}
              onClick={() => onWindowChange(w.key)}
            >
              {w.label}
            </Button>
          ))}
        </div>
      </div>

      {pending && !row && <div className="h-[300px] animate-pulse rounded-md bg-muted" />}

      {!row && !pending && (
        <p className="py-6 text-sm text-muted-foreground">
          {hist.error
            ? "This measure's history could not be loaded. Nothing is drawn rather than a partial line."
            : "No observations in this window."}
        </p>
      )}

      {row && (
        <>
          {hist.isFromCache && (
            <Badge variant="outline" className="gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5" />
              Showing saved data
            </Badge>
          )}

          {disagrees && (
            <div className="flex items-start gap-2 rounded-md border border-amber-500/40 px-3 py-2 text-xs">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
              <span>
                This chart reports {fmtMeasure(Number(row.current_value), unit)} where the row above
                reports {fmtMeasure(rowValue, rowUnit)}. They are the same observation and must
                agree, so one of the two is being computed from a different definition. Treat both
                as unreliable until that is resolved.
              </span>
            </div>
          )}

          {/* Every figure on this line belongs to the window on screen, which is why the window is
              named in the heading above them rather than left to the button state. */}
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
            <div>
              <dt className="text-xs text-muted-foreground">Latest, {fmtDate(row.as_of)}</dt>
              <dd className="tabular-nums font-semibold">
                {fmtMeasure(Number(row.current_value), unit)}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Percentile in this window</dt>
              <dd className="tabular-nums font-semibold">
                {Number(row.pctile).toFixed(0)}
                <span className="text-muted-foreground text-xs font-normal">/100</span>
              </dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Range in this window</dt>
              <dd className="tabular-nums">
                {fmtMeasure(Number(row.window_min), unit)} to{" "}
                {fmtMeasure(Number(row.window_max), unit)}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Median in this window</dt>
              <dd className="tabular-nums">{fmtMeasure(Number(row.window_median), unit)}</dd>
            </div>
          </dl>

          {series.length > 1 && (
            // min-w-0 and overflow-hidden keep the responsive chart from feeding its own width back
            // into the auto table layout of the table this panel is a cell of.
            <div className="min-w-0 overflow-hidden">
              <ChartContainer config={MEASURE_CHART_CONFIG} className="h-[300px] w-full aspect-auto">
                <LineChart data={series} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                  <XAxis
                    dataKey="t"
                    type="number"
                    scale="time"
                    domain={["dataMin", "dataMax"]}
                    tickFormatter={(v: number) => fmtAxisDate(v, spanYears > 12)}
                    axisLine={false} tickLine={false} tick={axisTickStyle} minTickGap={40}
                  />
                  <YAxis
                    domain={["auto", "auto"]}
                    tickFormatter={(v: number) => (unit === "bp" ? `${Math.round(v)}` : `${v.toFixed(1)}%`)}
                    axisLine={false} tickLine={false} tick={axisTickStyle} width={56}
                    label={unit === "bp"
                      ? { value: "bp", angle: -90, position: "insideLeft", fill: "hsl(var(--muted-foreground))", fontSize: 12 }
                      : undefined}
                  />
                  {/* Zero only when the window genuinely crosses it, so the axis is never stretched
                      over empty space to reach a line nothing touches. */}
                  {showZero && (
                    <ReferenceLine y={0} stroke="hsl(var(--foreground))" strokeOpacity={0.45} strokeDasharray="4 4" />
                  )}
                  {/* The median of THIS window, so the percentile beside the chart has something to
                      read against. Neutral, like the median tick on the collapsed track. */}
                  <ReferenceLine
                    y={Number(row.window_median)}
                    stroke="hsl(var(--muted-foreground))"
                    strokeOpacity={0.6}
                    strokeDasharray="2 4"
                    label={{ value: "median", position: "insideTopLeft", fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                  />
                  <ChartTooltip
                    content={<ChartTooltipContent
                      labelFormatter={(_l, p) => fmtDate(new Date(Number(p?.[0]?.payload?.t)).toISOString().slice(0, 10))}
                      formatter={(value) => [` ${fmtMeasure(Number(value), unit)}`, label]}
                    />}
                  />
                  <Line
                    type="linear" dataKey="v" name="v"
                    stroke="var(--color-v)" strokeWidth={1.75}
                    dot={false} activeDot={{ r: 4 }}
                    connectNulls={false} isAnimationActive={false}
                  />
                </LineChart>
              </ChartContainer>
            </div>
          )}

          {series.length <= 1 && (
            <p className="py-6 text-sm text-muted-foreground">
              One observation in this window, so there is no path to draw.
            </p>
          )}

          <div className="space-y-1 border-t pt-3 text-xs text-muted-foreground">
            {/* The true span, always, whatever the button says. window_from and window_to are the
                first and last real observation over all n_obs, not the ends of the sampled path. */}
            <p>
              {fmtCount(Number(row.n_obs))} observations, {fmtDate(row.window_from)} to{" "}
              {fmtDate(row.window_to)}. Percentile, range and median above are computed over exactly
              these observations and change with the window.
            </p>
            {row.requested_years == null ? (
              <p>
                Max is this measure's whole published history, which is a different length on every
                row: the series above begins {fmtDate(row.window_from)}. Nothing has been extended
                backwards to match another row.
              </p>
            ) : materiallyShort ? (
              <p>
                {choice.label} was requested, from {fmtDate(row.requested_from!)}, but this measure
                only reaches back to {fmtDate(row.window_from)}. The window drawn is the shorter
                one, and the figures above are computed over it rather than over the window asked
                for.
              </p>
            ) : null}
            {row.sampled && (
              <p>
                Drawn from {fmtCount(Number(row.n_points))} of those {fmtCount(Number(row.n_obs))}{" "}
                observations, evenly spaced and never averaged, with the most recent always kept.
                Every point is a real published observation.
              </p>
            )}
            <p>
              A day either leg did not publish is absent, not carried forward and not interpolated.
            </p>
            <SourceLines lines={sources} />
          </div>
        </>
      )}
    </div>
  );
}

// A plain curve-shape card: yield against maturity for a small set of curves, no toggles. Used for
// the two UK panels, which answer "what is this curve made of" rather than "how do countries
// compare".
//
// These cards have no as-of control of their own but silently follow the shape card's buttons,
// because they read the same query. Showing the date makes that visible, and the same banner covers
// the case where a BoE measure lags the others: all four UK curves come out of one zip, so they
// normally agree, but nothing in the pipeline guarantees it.
function CurveShapeCard<T extends string>({
  title, description, defs, series, footnote, asOfByCurve,
}: {
  title: string; description: string; defs: CurveDef<T>[];
  series: Record<string, number | null>[]; footnote?: string; asOfByCurve: AsOfByCurve;
}) {
  const lastIdx = {} as Record<string, number>;
  for (const d of defs) {
    lastIdx[d.code] = series.reduce((acc, p, i) => (p[d.code] != null ? i : acc), -1);
  }
  const maxTenor = series.length ? Number(series[series.length - 1].tenor) : 40;
  const config = configFor(defs);
  const groups = asOfGroups(defs.map((d) => d.code), asOfByCurve);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <AsOfBanner groups={groups} />
        {series.length === 0 ? (
          <div className="h-[320px] animate-pulse rounded-md bg-muted" />
        ) : (
          <ChartContainer config={config} className="h-[320px] w-full aspect-auto">
            <LineChart data={series} margin={{ top: 8, right: 56, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
              <XAxis
                dataKey="tenor" type="number" scale="sqrt" domain={[0, maxTenor]}
                ticks={[0.25, 1, 2, 5, 10, 20, 30, 40].filter((t) => t <= maxTenor)}
                tickFormatter={fmtTenor} axisLine={false} tickLine={false}
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                label={{ value: "Maturity", position: "insideBottom", offset: -4, fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
              />
              <YAxis
                domain={["auto", "auto"]} tickFormatter={(v: number) => `${v.toFixed(1)}%`}
                axisLine={false} tickLine={false}
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }} width={56}
              />
              <ChartTooltip content={<ChartTooltipContent
                labelFormatter={(v) => `Maturity ${fmtTenor(Number(v))}`}
                formatter={(value, name) => [
                  ` ${fmtPct(Number(value))}`,
                  seriesLabelWithDate(String(name), config[name as string]?.label, groups, asOfByCurve),
                ]}
              />} />
              <ChartLegend verticalAlign="bottom" content={<ChartLegendContent />} />
              {defs.map((d) => (
                <Line key={d.code} type="monotone" dataKey={d.code} name={d.code}
                  stroke={`var(--color-${d.code})`} strokeWidth={2}
                  dot={{ r: 3, strokeWidth: 0, fill: `var(--color-${d.code})` }}
                  activeDot={{ r: 5 }} connectNulls isAnimationActive={false}>
                  <LabelList dataKey={d.code} content={<EndLabel text={d.short} at={lastIdx[d.code]} />} />
                </Line>
              ))}
            </LineChart>
          </ChartContainer>
        )}
        {footnote && (
          <p className="mt-4 border-t pt-3 text-xs text-muted-foreground">{footnote}</p>
        )}
      </CardContent>
    </Card>
  );
}

// One attribution line per publisher. Shared by the curve cards and by both strips, so the page
// keeps a single attribution style instead of growing a second one alongside the first.
function SourceLines({ lines }: { lines: { label: string; source: string; asOf?: string }[] }) {
  return (
    <>
      {lines.map((l) => (
        <div key={l.label} className="flex flex-wrap gap-x-2">
          <span className="font-medium">{l.label}:</span>
          <span>{l.source}</span>
          {l.asOf && <span>as at {fmtDate(l.asOf)}</span>}
        </div>
      ))}
    </>
  );
}

function SourceFooter({ asOfByCurve, sampled, alignment }: {
  asOfByCurve: AsOfByCurve;
  sampled?: boolean;
  alignment?: { lastCommon: string | null; ahead: { label: string; date: string }[] } | null;
}) {
  return (
    <div className="mt-4 space-y-1 border-t pt-3 text-xs text-muted-foreground">
      {sampled && (
        <div className="pb-1">
          Long windows are drawn from an evenly spaced subset of real trading days, never an
          average. The most recent observation is always included.
        </div>
      )}
      {alignment && (
        <div className="pb-1">
          Every line is plotted only on days all four curves published, so a point compares four
          markets on one trading day. A day a market was shut is left out rather than carried
          forward.
          {alignment.ahead.length > 0 && alignment.lastCommon && (
            <>
              {" "}
              {joinLabels(alignment.ahead.map((a) => a.label))}{" "}
              {alignment.ahead.length === 1 ? "has" : "have"} published to{" "}
              {joinLabels([...new Set(alignment.ahead.map((a) => fmtDate(a.date)))])}, past the
              last day all four shared ({fmtDate(alignment.lastCommon)}). Those points are drawn at
              their own date and are the only ones here not shared by every curve.
            </>
          )}
        </div>
      )}
      <SourceLines
        lines={CURVES.map((c) => ({
          label: c.label, source: CURVE_ATTRIBUTION[c.code].source, asOf: asOfByCurve[c.code],
        }))}
      />
    </div>
  );
}
